import * as R from 'ramda';
import * as Functional from '../functional';
import * as Feature from './feature';
import LatLng from './latlng';

const _centerOfPolygon = polygonFeature => {
    const coordinates = R.splitEvery(2, R.flatten(polygonFeature.geometry.coordinates));
    const xs = R.map(R.head, coordinates);
    const ys = R.map(R.last, coordinates);

    return [(Functional.min(xs) + Functional.max(xs)) / 2, (Functional.min(ys) + Functional.max(ys)) / 2];
};

const guessLatLng = (featuresForEntity, addingText) => {
    const badgeFeature = R.find(Feature.hasBadge, featuresForEntity);
    const textFeature = R.find(Feature.hasText, featuresForEntity);
    const polygonFeature = R.find(Feature.isPolygon, featuresForEntity);

    if (!badgeFeature && !textFeature && !polygonFeature) {
        throw new Error("addFeature: no latLng was passed in and there's no way to guess it");
    }

    const copyPointFromBadge = addingText && !!badgeFeature;
    const copyPointFromText = !addingText && !!textFeature;

    let lng;
    let lat;
    if (copyPointFromBadge) [lng, lat] = [badgeFeature.centerPoint.x, badgeFeature.centerPoint.y];
    else if (copyPointFromText) [lng, lat] = textFeature.geometry.coordinates;
    else if (polygonFeature) [lng, lat] = _centerOfPolygon(polygonFeature);

    return LatLng.fromLatAndLng(lat, lng);
};

/*
 * Retun an "instructions" object that describes the additional Feature to add for the Entity
 * @sig :: instructionsForAddEntity :: ({ Theme, Entity, Boolean, Boolean, LatLng }) -> { addFeatures: [Feature] }
 */
const instructionsForAddEntity = ({ theme, featuresForEntity, entity, showBadge, showName, latLng, features }) => {
    if (!latLng) latLng = guessLatLng(featuresForEntity, showName);

    const { id: entityId, category, name: text } = entity;
    const feature = Feature.symbolFeatureFromData({
        category,
        entityId,
        features,
        latLng,
        showBadge,
        showText: showName,
        text,
        theme,
    });
    return { addFeatures: [feature] };
};

/*
 * Return instructions that will remove point Features for the Entity and reset the polygon Feature to "unoccupied"
 * @sig :: instructionsForRemoveEntity :: [Feature]-> { removeFeatures:, updateFeatures: }
 */
const instructionsForRemoveEntity = (featuresForEntity, removePolygon) => {
    if (removePolygon) return { removeFeatures: featuresForEntity };

    const setFeatureProperties = toAdd => f =>
        Functional.overProp('properties', props => R.mergeRight(props, toAdd), f);

    const polygons = R.filter(Functional.pathIs(['geometry', 'type'], 'Polygon'), featuresForEntity);
    const toBeRemoved = R.reject(Functional.pathIs(['geometry', 'type'], 'Polygon'), featuresForEntity);

    // TODO: get the "real" colors for "unoccupied" from the theme rather than hard-coding them
    const polygonsToUpdate = R.map(
        setFeatureProperties({
            category: 'unoccupied',
            fillColor: '#DFE5E7',
            strokeColor: '#CBD0D9',
        }),
        polygons,
    );

    return {
        removeFeatures: toBeRemoved,
        updateFeatures: polygonsToUpdate,
    };
};

/*
 * Compute what we should do given:
 *
 * - the existing state of the Features related to an Entity
 * - the requested state (show/hide badge and/or name)
 *
 * The result will be one of:
 *
 * - remove        remove the symbol feature (as long as there is also a polygon)
 * - illegalRemove tried to remove the symbol feature, but there was no polygon
 * - add           add a new symbol feature (if there is none already)
 * - update        modify the existing symbol feature
 */
const _computeUpdateDisposition = ({ /* entity, */ featuresForEntity, showBadge, showName }) => {
    const symbolFeature = R.find(f => f.geometry.type === 'Point', featuresForEntity);
    const symbolHasBadge = R.path(['properties', 'showBadge'], symbolFeature);
    const symbolHasText = R.path(['properties', 'text'], symbolFeature);

    const symbolHasJustBadge = symbolHasBadge && !symbolHasText;
    const symbolHasJustText = symbolHasText && !symbolHasBadge;
    const symbolHasBoth = symbolHasBadge && symbolHasText;

    const removeBoth = !showName && !showBadge;
    const removeSymbol =
        (symbolHasBoth && removeBoth) || (symbolHasJustBadge && !showBadge) || (symbolHasJustText && !showName);
    const addSymbol = showName || showBadge;

    const shouldRemove = symbolFeature && removeSymbol && !addSymbol;
    const isIllegalRemove = shouldRemove && featuresForEntity.length === 1;
    const shouldAdd = !symbolFeature && addSymbol;

    if (isIllegalRemove) return 'illegalRemove';
    if (shouldRemove) return 'remove';
    if (shouldAdd) return 'add';
    if (symbolFeature) return 'update';
};

/*
 * Return an "instructions" object that describes what actions to take to update the Entity. Changes could be
 * adds, removes or updates depending on the previous and current state of the Entity itself, along with the
 * additional parameters
 *
 * showBadge and showName default to the previous values found in the symbol Feature for the Entity (if there is one)
 *
 * @sig :: instructionsForUpdateEntity :: {Theme, Entity, [Feature], Boolean, Boolean, LatLng }-> Object
 */
const instructionsForUpdateEntity = ({ theme, entity, featuresForEntity, showBadge, showName, latLng }) => {
    const pushInstructionsForUpdateFeature = feature => {
        feature = Feature.update({
            theme,
            feature,
            showBadge,
            showText: showName,
            category: entity.category,
            text: entity.name,
            latLng,
        });

        if (!showBadge || symbolHasLogo) feature = Functional.omitAtPath(['properties', 'showBadge'], feature);
        instructions.updateFeatures.push(feature);
    };

    let instructions = {
        addFeatures: [],
        removeFeatures: [],
        updateFeatures: [],
    };

    // find features to work on
    const polygonFeature = R.find(f => f.geometry.type === 'Polygon', featuresForEntity);
    const symbolFeature = R.find(f => f.geometry.type === 'Point', featuresForEntity);
    const symbolHasBadge = Functional.hasPath(['properties', 'showBadge'], symbolFeature);
    const symbolHasText = Functional.hasPath(['properties', 'text'], symbolFeature);
    const symbolHasLogo = Functional.hasPath(['properties', 'logo'], symbolFeature);
    const symbolHasBadgeOrLogo = symbolHasBadge || symbolHasLogo;

    // default to previous state of showBadge/showName
    showBadge = R.isNil(showBadge) ? symbolHasBadgeOrLogo : !!showBadge;
    showName = R.isNil(showName) ? symbolHasText : !!showName;

    // update the polygon subfeature
    if (polygonFeature) pushInstructionsForUpdateFeature(polygonFeature);

    const illegal = "Can't remove the last feature related to an entity via updateElement; use removeAiEntity instead";
    const dataForAdd = { theme, featuresForEntity, entity, showBadge, showName, latLng };

    // "manage" the point/symbol subfeature
    switch (_computeUpdateDisposition({ entity, featuresForEntity, showBadge, showName })) {
        case 'add':
            instructions = instructionsForAddEntity(dataForAdd);
            break;
        case 'remove':
            instructions = instructionsForRemoveEntity([symbolFeature]);
            break;
        case 'update':
            pushInstructionsForUpdateFeature(symbolFeature);
            break;
        case 'illegalRemove':
            throw new Error(illegal);
        default:
            throw new Error(`updateEntity: don't understand what to do with ${JSON.stringify(entity)}`);
    }

    return instructions;
};

const createDefaultEntity = (latLng, level, structure, ordinalNumber, includeBadge, showName, security) => {
    const { lat, lng } = latLng;

    const entity = {
        category: 'shop', // TODO: need default badge...using shop for now
        floor: `${ordinalNumber} ${level.details}`,
        name: 'Enter POI Name',
        ordinalNumber,
        position: { lat, lng, levelId: level.id },
        showName,
        terminal: structure.name,
        uuid: `${level.venueId}_NEW`,
        latLng,
        levelId: level.id,
        venueId: level.venueId,
    };

    if (includeBadge) entity.showBadge = true;
    if (security) entity.security = security;

    return entity;
};

/*
 * Check if the entity had its name, category or status changed so the map needs to be updated
 * checkEntityForMapChanges :: (Entity, Entity) -> Boolean
 */
const checkEntityForMapChanges = (entity, updatedEntity) => {
    const hasMapChanged =
        (updatedEntity.name && entity.name !== updatedEntity.name) ||
        (updatedEntity.category && entity.category !== updatedEntity.category) ||
        updatedEntity.id.includes('#');
    return hasMapChanged;
};

/* Enrich the update object with levelId, showBadge, showName and hasMapChanged.
 * This information is needed by the RMS to update the AI JSON
 * @sig enrichUpdateEntity :: (Entity, Object, [Feature], LevelId) -> Entity
 */
const enrichUpdateEntity = (entity, updateEntityObject, featuresForEntity, levelId) => {
    const point = R.find(feature => feature.geometry.type === 'Point', featuresForEntity);
    const showBadge = R.pathOr(false, ['properties', 'showBadge'], point);
    const showName = R.hasPath(['properties', 'text'], point);

    return R.mergeRight(updateEntityObject, {
        levelId,
        showBadge,
        showName,
        hasMapChanged: checkEntityForMapChanges(entity, updateEntityObject.entity),
    });
};

const getEntityCategory = entity => entity.category;
const getIcon = entity => entity.icon;

const convertToLower = entity => (entity.category === 'Gate' ? R.assoc('category', 'gate', entity) : entity);
const entityIdToNumber = Functional.pipe(R.prop('id'), R.replace(/#/g, ''), parseInt); // :: Entity -> Number
const addNumericIdToEntity = Functional.assocUsingFunc('numericId', entityIdToNumber); // :: Entity -> Entity

// Convert entity.id to a numericId ignoring an optional '#' character marking it "closed"
const addNumericIdsToEntities = entities => R.map(addNumericIdToEntity, entities);

/*
 * Convert 'Gate' to 'gate'
 * @sig convertGateToLower :: [Entity] -> [Entity]
 */
const convertGateToLower = entities => R.map(convertToLower, entities);

const enrichCreatedEntity = entity => {
    const enrich = R.pipe(convertToLower, addNumericIdToEntity);

    return enrich(entity);
};

const isClosed = entity => R.propOr('', 'id', entity).match(/#/);

export {
    addNumericIdsToEntities,
    convertGateToLower,
    createDefaultEntity,
    enrichCreatedEntity,
    enrichUpdateEntity,
    instructionsForAddEntity,
    instructionsForUpdateEntity,
    instructionsForRemoveEntity,
    getEntityCategory,
    getIcon,
    guessLatLng,
    isClosed,
};
